import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, UserPlus, Search, Upload, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { CrmAuth, crmApi, formatApiError, PasswordInput, ChangePasswordForm, ForcedPasswordChange } from "@/lib/portalClient";

/**
 * CRM back-office — for Pali Hill City sales/CRM staff.
 * Distinct from /admin (site analytics) and from /portal (customer-facing).
 * Auth: JWT audience=crm_staff, seeded from CRM_ADMIN_EMAIL / CRM_ADMIN_PASSWORD.
 */

export default function CrmApp() {
  return (
    <CrmAuth.Provider>
      <Toaster position="top-center" toastOptions={{ style: { background: "#0B4A3D", color: "#F4F4EF", border: "1px solid rgba(166,206,57,0.4)" } }} />
      <Routes>
        <Route path="/login" element={<CrmLogin />} />
        <Route element={<CrmShell />}>
          <Route index element={<CustomersList />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="bulk-upload" element={<BulkUpload />} />
        </Route>
      </Routes>
    </CrmAuth.Provider>
  );
}

function CrmLogin() {
  const { user, refresh } = CrmAuth.useCtx();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  if (user && user !== false) return <Navigate to="/crm" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await crmApi.post("/auth/login", { email, password });
      await refresh();
      navigate("/crm");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-offwhite flex items-center justify-center px-4 py-16" data-testid="crm-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="eyebrow text-teal mb-2">Pali Hill City · CRM</div>
          <h1 className="font-display text-forest text-3xl">Staff sign in</h1>
        </div>
        <form onSubmit={submit} className="bg-white border border-forest/15 rounded-lg p-8 space-y-4">
          <div><label className="text-xs uppercase tracking-widest text-forest/60">Email</label>
            <input data-testid="crm-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite" /></div>
          <div><label className="text-xs uppercase tracking-widest text-forest/60">Password</label>
            <PasswordInput testId="crm-login-password" value={password} onChange={setPassword} autoComplete="current-password" /></div>
          {error && <div className="text-red-600 text-sm" data-testid="crm-login-error">{error}</div>}
          <button disabled={busy} data-testid="crm-login-submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest text-cream text-sm disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

import { Outlet, useLocation } from "react-router-dom";

function CrmShell() {
  const { user, logout, refresh } = CrmAuth.useCtx();
  const [pwOpen, setPwOpen] = useState(false);
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-forest" /></div>;
  if (user === false) return <Navigate to="/crm/login" replace />;

  // Forced first-login reset — CRM staff must replace a temporary password
  // before they can touch the back-office.
  if (user.must_change_password) {
    return (
      <ForcedPasswordChange
        api={crmApi}
        audienceLabel="Pali Hill City · CRM"
        testId="crm-forced-change"
        onDone={refresh}
        onLogout={logout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-offwhite" data-testid="crm-shell">
      <header className="bg-forest text-cream">
        <div className="container-editorial flex items-center justify-between py-4">
          <div>
            <div className="eyebrow text-lime">CRM Back-Office</div>
            <div className="font-display text-2xl mt-0.5">Customers</div>
          </div>
          <div className="flex items-center gap-4 text-sm text-cream/80">
            <Link to="/crm" className="hover:text-cream" data-testid="crm-nav-customers">Customers</Link>
            <Link to="/crm/bulk-upload" className="hover:text-cream" data-testid="crm-nav-bulk">Bulk upload</Link>
            <span className="text-cream/50">·</span>
            <span>{user?.email}</span>
            <button onClick={() => setPwOpen(true)} data-testid="crm-change-password-open" className="inline-flex items-center gap-1 hover:text-cream"><KeyRound size={14} /> Change password</button>
            <button onClick={logout} data-testid="crm-logout" className="inline-flex items-center gap-1 hover:text-cream"><LogOut size={14} /> Log out</button>
          </div>
        </div>
      </header>
      <div className="container-editorial py-8"><Outlet /></div>

      {pwOpen && (
        <div className="fixed inset-0 bg-forest/60 flex items-center justify-center px-4 z-50" onClick={() => setPwOpen(false)} data-testid="crm-change-password-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow text-teal">Change your password</div>
            <ChangePasswordForm api={crmApi} testIdPrefix="crm-change-pw" onDone={() => setPwOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function CustomersList() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await crmApi.get("/customers", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) { if (!silent) toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { if (!silent) setLoading(false); }
  };

  // Poll every 15s so new customer activity/accounts show up without
  // logging out and back in.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const iv = setInterval(() => load(true), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [q]);

  const resetPw = async (r) => {
    try {
      await crmApi.post(`/customers/${r.id}/reset-password`);
      toast.success(`New temporary password emailed to ${r.email}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const removeRow = async (r) => {
    if (!confirm(`Remove ${r.name} (${r.customer_id}) from the portal? Their records stay in the system, but they lose login access.`)) return;
    try {
      await crmApi.delete(`/customers/${r.id}`);
      toast.success(`${r.name} removed`);
      load(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="crm-customers-list">
      <div className="flex items-center justify-between">
        <div className="relative w-96">
          <Search size={16} className="absolute top-1/2 -translate-y-1/2 left-3 text-forest/50" />
          <input placeholder="Search name / email / mobile / ID" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-full border border-forest/20 rounded-full pl-9 pr-4 py-2 text-sm bg-white" data-testid="crm-customers-search" />
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-forest text-cream text-sm" data-testid="crm-create-customer-open">
          <UserPlus size={14} /> Create customer
        </button>
      </div>

      <div className="bg-white border border-forest/15 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-forest/5 text-left text-xs uppercase text-forest/60">
            <tr><th className="p-3">Customer ID</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Mobile</th><th className="p-3">Plots</th><th className="p-3">Created</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-6 text-center text-forest/50">Loading…</td></tr> :
              rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-forest/50">No customers yet.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-forest/10 hover:bg-forest/5 cursor-pointer" onClick={() => window.location.assign(`/crm/customers/${r.id}`)} data-testid={`crm-customer-row-${r.id}`}>
                    <td className="p-3 font-mono text-forest">{r.customer_id}</td>
                    <td className="p-3">{r.name}</td>
                    <td className="p-3 text-forest/80">{r.email}</td>
                    <td className="p-3 text-forest/80">{r.mobile}</td>
                    <td className="p-3">{(r.plot_numbers || []).join(", ")}</td>
                    <td className="p-3 text-forest/60 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-right whitespace-nowrap space-x-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setEditRow(r)} className="text-teal text-xs underline" data-testid={`crm-edit-${r.id}`}>Edit</button>
                      <button onClick={() => resetPw(r)} className="text-teal text-xs underline" data-testid={`crm-reset-${r.id}`}>Reset password</button>
                      <button onClick={() => removeRow(r)} className="text-red-600 text-xs underline" data-testid={`crm-remove-${r.id}`}>Remove</button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {createOpen && <CreateCustomerModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {editRow && <EditCustomerModal customer={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />}

      <TeamAccessPanel />
    </div>
  );
}

function EditCustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: customer.name || "",
    mobile: customer.mobile || "",
    plot_count: customer.plot_count || 1,
    plot_numbers: (customer.plot_numbers || []).join(", "),
    booking_amount: customer.booking_amount ?? "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await crmApi.patch(`/customers/${customer.id}`, {
        name: form.name,
        mobile: form.mobile,
        plot_count: Number(form.plot_count),
        plot_numbers: form.plot_numbers.split(",").map((s) => s.trim()).filter(Boolean),
        booking_amount: form.booking_amount === "" ? null : Number(form.booking_amount),
      });
      toast.success("Customer updated");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-forest/60 flex items-center justify-center px-4 z-50" onClick={onClose} data-testid="crm-edit-customer-modal">
      <form onSubmit={submit} className="bg-white rounded-lg p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow text-teal">Edit customer · {customer.customer_id}</div>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-edit-name" />
        <input required placeholder="Mobile (+91...)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-edit-mobile" />
        <div className="grid grid-cols-3 gap-3">
          <input required type="number" min={1} placeholder="# plots" value={form.plot_count} onChange={(e) => setForm({ ...form, plot_count: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite col-span-1" data-testid="crm-edit-count" />
          <input required placeholder="Plot numbers (comma-sep)" value={form.plot_numbers} onChange={(e) => setForm({ ...form, plot_numbers: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite col-span-2" data-testid="crm-edit-plots" />
        </div>
        <input type="number" min="0" placeholder="Booking amount (₹)" value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-edit-booking-amount" />
        <button disabled={busy} className="w-full px-4 py-2 rounded-full bg-forest text-cream text-sm disabled:opacity-50" data-testid="crm-edit-submit">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}

// ---------- Team access: reset passwords for any account -------------------
function TeamAccessPanel() {
  const [staff, setStaff] = useState([]);
  const [busyEmail, setBusyEmail] = useState("");

  useEffect(() => {
    crmApi.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const reset = async (email) => {
    setBusyEmail(email);
    try {
      await crmApi.post("/staff/reset-password", { email });
      toast.success(`New temporary password emailed to ${email}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusyEmail(""); }
  };

  return (
    <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="crm-team-access">
      <div className="eyebrow text-teal mb-1">Team access · password resets</div>
      <p className="text-xs text-forest/60 mb-4">Reset any account's password from here — staff get a fresh temporary password by email and must set a new one on next login. Customer passwords are reset from each customer's page.</p>
      <ul className="divide-y divide-forest/10">
        {staff.map((s) => (
          <li key={s.email} className="py-2 flex items-center justify-between text-sm">
            <div>
              <div className="text-forest">{s.name || s.email}</div>
              <div className="text-xs text-forest/50">{s.email} · {s.role}</div>
            </div>
            <button
              onClick={() => reset(s.email)}
              disabled={busyEmail === s.email}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-forest/20 text-sm text-forest hover:bg-forest/5 disabled:opacity-50"
              data-testid={`crm-staff-reset-${s.email}`}
            >
              {busyEmail === s.email ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              Reset password
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", plot_count: 1, plot_numbers: "", booking_amount: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const plots = form.plot_numbers.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        plot_count: Number(form.plot_count),
        plot_numbers: plots,
        booking_amount: form.booking_amount === "" ? null : Number(form.booking_amount),
      };
      await crmApi.post("/customers", payload);
      toast.success("Customer created — welcome email sent.");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-forest/60 flex items-center justify-center px-4 z-50" onClick={onClose} data-testid="crm-create-customer-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg max-w-md w-full p-6 space-y-3">
        <div className="eyebrow text-teal">New customer</div>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-create-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-create-email" />
        <input required placeholder="Mobile (+91...)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-create-mobile" />
        <div className="grid grid-cols-3 gap-3">
          <input required type="number" min={1} placeholder="# plots" value={form.plot_count} onChange={(e) => setForm({ ...form, plot_count: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite col-span-1" data-testid="crm-create-count" />
          <input required placeholder="Plot numbers (comma-sep)" value={form.plot_numbers} onChange={(e) => setForm({ ...form, plot_numbers: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite col-span-2" data-testid="crm-create-plots" />
        </div>
        <input type="number" min="0" placeholder="Booking amount (₹) — included in the welcome email" value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-create-booking-amount" />
        <div className="text-xs text-forest/60">Customer ID + password generated automatically. Welcome email sent immediately.</div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-forest/20 text-sm">Cancel</button>
          <button disabled={busy} className="flex-1 px-4 py-2 rounded-full bg-forest text-cream text-sm disabled:opacity-50" data-testid="crm-create-submit">
            {busy ? "Creating…" : "Create + send welcome email"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState("");

  const load = async (silent = false) => {
    try {
      const { data } = await crmApi.get(`/customers/${id}`);
      setData((prev) => {
        // Real-time-ish activity nudge: if a poll picks up NEW customer
        // activity while the page is open, say so instead of silently
        // swapping the list under the reader.
        if (silent && prev) {
          const prevIds = new Set((prev.activity || []).map((a) => a.id));
          const fresh = (data.activity || []).filter((a) => !prevIds.has(a.id) && a.actor === "customer");
          if (fresh.length) toast.info(`New customer activity: ${fresh[0].action}`);
        }
        return data;
      });
    } catch (e) { if (!silent) toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  // Live refresh — customer uploads / saves show up without re-login.
  useEffect(() => {
    const iv = setInterval(() => load(true), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [id]);

  const upload = async (category, kind, file) => {
    setUploading(`${category}/${kind}`);
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    try {
      await crmApi.post(`/customers/${id}/uploads/${category}`, fd);
      toast.success("Uploaded — customer notified.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const regenPassword = async () => {
    if (!confirm("Regenerate password + email the customer? This invalidates their current password.")) return;
    try {
      await crmApi.post(`/customers/${id}/reset-password`);
      toast.success("New password sent to customer.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const removeFile = async (f) => {
    if (!confirm(`Remove "${f.original_filename}"? The customer will no longer see it.`)) return;
    try {
      await crmApi.delete(`/files/${f.id}`);
      toast.success("Document removed");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!data) return <div className="text-forest/60">Loading…</div>;
  const c = data.customer;
  const files = data.files || [];
  const filesBy = (cat) => files.filter((f) => f.category === cat);

  return (
    <div className="space-y-6" data-testid="crm-customer-detail">
      <Link to="/crm" className="inline-flex items-center gap-1 text-teal text-sm underline underline-offset-4"><ArrowLeft size={14} /> Back to customers</Link>

      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display text-forest text-2xl">{c.name}</div>
            <div className="text-forest/60 text-sm mt-1">{c.customer_id} · {c.email} · {c.mobile}</div>
            <div className="text-forest/60 text-sm">Plot(s): {(c.plot_numbers || []).join(", ")}</div>
          </div>
          <button onClick={regenPassword} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-forest/20 text-sm" data-testid="crm-regen-password">
            <KeyRound size={14} /> Regenerate password
          </button>
        </div>
      </div>

      <CrmProfileCard profile={data.profile} />

      {/* Customer-uploaded documents (KYC, profile photo, cancelled cheque)
          are shown here as a read-only pane so the CRM can view/download
          without navigating away. Uploads for these categories happen in
          the customer portal, not here. */}
      {["kyc", "profile", "refund_bank", "loan_sanction", "payment_proof"].map((cat) => {
        const items = files.filter((f) => f.category === cat);
        if (items.length === 0) return null;
        const label = cat === "kyc" ? "Customer KYC uploads" : cat === "profile" ? "Customer profile uploads (photo / relation proofs)" : cat === "loan_sanction" ? "Bank loan sanction letter (customer upload)" : cat === "payment_proof" ? "Payment confirmations (customer upload)" : "Refund bank details (cancelled cheque)";
        return (
          <div key={cat} className="bg-white border border-forest/15 rounded-lg p-6" data-testid={`crm-customer-${cat}`}>
            <div className="eyebrow text-teal mb-3">{label}</div>
            <ul className="divide-y divide-forest/10">
              {items.map((f) => (
                <li key={f.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-forest">{f.original_filename}</div>
                    <div className="text-xs text-forest/50">{f.kind} · {new Date(f.created_at).toLocaleString()}</div>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline">Download</a>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {[
        { cat: "booking_form", label: "Booking form" },
        { cat: "cost_sheet", label: "Cost sheet" },
        { cat: "receipt", label: "Payment receipts" },
        { cat: "draft", label: "Draft agreement" },
      ].map((s) => (
        <div key={s.cat} className="bg-white border border-forest/15 rounded-lg p-6" data-testid={`crm-doc-section-${s.cat}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow text-teal">{s.label}</div>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-forest/20 text-sm cursor-pointer">
              {uploading === `${s.cat}/${s.cat}` ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => e.target.files?.[0] && upload(s.cat, s.cat, e.target.files[0])} data-testid={`crm-upload-${s.cat}`} />
            </label>
          </div>
          {filesBy(s.cat).length === 0 ? (
            <div className="text-sm text-forest/50">Nothing uploaded yet.</div>
          ) : (
            <ul className="divide-y divide-forest/10">
              {filesBy(s.cat).map((f) => (
                <li key={f.id} className="py-2 text-sm flex items-center justify-between gap-4">
                  <span className="text-forest">{f.original_filename}</span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-forest/50">{new Date(f.created_at).toLocaleString()} · {f.uploaded_by}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline text-xs" data-testid={`crm-file-view-${f.id}`}>View</a>
                    <button onClick={() => removeFile(f)} className="text-red-600 underline text-xs" data-testid={`crm-file-remove-${f.id}`}>Remove</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      <PaymentScheduleEditor customerId={id} payments={data.payments || []} onChange={load} />

      <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="crm-customer-tickets">
        <div className="eyebrow text-teal mb-3">Support tickets</div>
        {(data.tickets || []).length === 0 ? <div className="text-sm text-forest/50">No tickets raised.</div> :
          <ul className="divide-y divide-forest/10">
            {(data.tickets || []).map((t) => (
              <TicketRow key={t.id} ticket={t} customerId={id} onReplied={load} />
            ))}
          </ul>}
      </div>

      <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="crm-customer-activity">
        <div className="eyebrow text-teal mb-3">Activity</div>
        {(data.activity || []).length === 0 ? <div className="text-sm text-forest/50">No activity yet.</div> :
          <ul className="space-y-2">
            {(data.activity || []).map((a) => (
              <li key={a.id} className="text-sm flex items-start gap-3">
                <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${a.actor === "crm" ? "bg-lime" : "bg-teal"}`} />
                <div>
                  <div className="text-forest">{a.action}</div>
                  <div className="text-xs text-forest/50">{a.actor} · {new Date(a.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>}
      </div>
    </div>
  );
}


// ---------- Payment schedule editor (Phase 2) -----------------------------
const PAYMENT_ACCOUNTS = ["Towards Agreement Value", "Towards Development & GST", "Towards Stamp Duty"];

// ---------- Customer profile (portal-filled details, read-only for CRM) ------
function ProfileField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-forest/50">{label}</div>
      <div className="text-sm text-forest whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function ApplicantBlock({ title, a, testId }) {
  if (!a || !Object.values(a).some(Boolean)) return null;
  return (
    <div className="border-t border-forest/10 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0" data-testid={testId}>
      <div className="eyebrow text-teal mb-2">{title}</div>
      <div className="grid md:grid-cols-3 gap-x-6 gap-y-3">
        <ProfileField label="Full name" value={a.full_name} />
        <ProfileField label="Date of birth" value={a.dob} />
        <ProfileField label="PAN" value={a.pan} />
        <ProfileField label="Aadhar" value={a.aadhar} />
        <ProfileField label="Email" value={a.email} />
        <ProfileField label="Mobile" value={a.mobile} />
        <ProfileField label="Postal address" value={a.postal_address} />
      </div>
      {a.photo_id && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${a.photo_id}`} target="_blank" rel="noopener noreferrer" className="text-teal text-xs underline mt-2 inline-block" data-testid={`${testId}-photo`}>View photo</a>
      )}
    </div>
  );
}

function CrmProfileCard({ profile }) {
  const p = profile || {};
  const coList = (p.co_applicants && p.co_applicants.length)
    ? p.co_applicants
    : (p.co_applicant ? [{ applicant: p.co_applicant, relation: p.co_applicant_relation, relation_proof_id: p.co_applicant_relation_proof_id }] : []);
  const bank = p.bank_details || {};
  const hasAny = Object.keys(p).length > 0 && (p.primary || coList.length || Object.values(bank).some(Boolean));

  return (
    <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="crm-profile-card">
      <div className="eyebrow text-teal mb-3">Customer profile & KYC details (from portal)</div>
      {!hasAny ? (
        <div className="text-sm text-forest/50">Customer hasn't filled their profile yet.</div>
      ) : (
        <>
          <ApplicantBlock title="Primary applicant" a={p.primary || {}} testId="crm-profile-primary" />
          {coList.map((co, i) => (
            <div key={i}>
              <ApplicantBlock title={`Co-applicant ${i + 1}`} a={co.applicant || {}} testId={`crm-profile-co-${i}`} />
              <div className="grid md:grid-cols-3 gap-x-6 gap-y-3 mt-2">
                <ProfileField label="Relation to primary" value={co.relation} />
              </div>
              {co.relation_proof_id && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${co.relation_proof_id}`} target="_blank" rel="noopener noreferrer" className="text-teal text-xs underline mt-1 inline-block" data-testid={`crm-profile-co-${i}-proof`}>View relation proof</a>
              )}
            </div>
          ))}
          {Object.values(bank).some(Boolean) && (
            <div className="border-t border-forest/10 pt-3 mt-3" data-testid="crm-profile-bank">
              <div className="eyebrow text-teal mb-2">Refund bank details</div>
              <div className="grid md:grid-cols-3 gap-x-6 gap-y-3">
                <ProfileField label="Account holder" value={bank.account_holder} />
                <ProfileField label="Account number" value={bank.account_number} />
                <ProfileField label="IFSC" value={bank.ifsc} />
                <ProfileField label="Bank" value={bank.bank_name} />
              </div>
              {bank.cancelled_cheque_id && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${bank.cancelled_cheque_id}`} target="_blank" rel="noopener noreferrer" className="text-teal text-xs underline mt-2 inline-block" data-testid="crm-profile-cheque">View cancelled cheque</a>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Support ticket row with CRM reply --------------------------------
function TicketRow({ ticket: t, customerId, onReplied }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (reply.trim().length < 2) return toast.error("Write a reply first");
    setBusy(true);
    try {
      await crmApi.post(`/customers/${customerId}/tickets/${t.id}/reply`, { message: reply.trim() });
      toast.success("Reply sent to the customer — ticket resolved.");
      setReply("");
      onReplied?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <li className="py-3 text-sm" data-testid={`crm-ticket-${t.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="text-forest font-body">{t.subject}</div>
        <span className={`text-xs px-2.5 py-1 rounded-full flex-shrink-0 ${t.status === "open" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`} data-testid={`crm-ticket-status-${t.id}`}>{t.status === "open" ? "Open" : t.status === "resolved" ? "Resolved" : t.status}</span>
      </div>
      <div className="text-xs text-forest/50 mt-1">{t.category} · {new Date(t.created_at).toLocaleString()}</div>
      <div className="text-forest/70 mt-1 whitespace-pre-wrap">{t.message}</div>
      {(t.replies || []).map((r, i) => (
        <div key={i} className="mt-2 border-l-2 border-lime bg-offwhite rounded-r-md px-3 py-2" data-testid={`crm-ticket-reply-${t.id}-${i}`}>
          <div className="text-forest whitespace-pre-wrap">{r.message}</div>
          <div className="text-xs text-forest/50 mt-1">{r.by} · {new Date(r.at).toLocaleString()}</div>
        </div>
      ))}
      {t.status === "open" && (
        <div className="mt-3 space-y-2">
          <textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply — it's emailed to the customer and the ticket is marked Resolved."
            className="w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite text-sm"
            data-testid={`crm-ticket-reply-input-${t.id}`}
          />
          <button
            onClick={send}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-cream text-xs disabled:opacity-50"
            data-testid={`crm-ticket-reply-send-${t.id}`}
          >
            {busy && <Loader2 size={12} className="animate-spin" />} Send reply & resolve
          </button>
        </div>
      )}
    </li>
  );
}

const EMPTY_PAYMENT_ROW = { milestone: "", due_date: "", amount: "", account: "Towards Agreement Value" };

function PaymentScheduleEditor({ customerId, payments, onChange }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState([{ ...EMPTY_PAYMENT_ROW }]);
  const [replace, setReplace] = useState(false);

  const addRow = () => setDraft((d) => [...d, { ...EMPTY_PAYMENT_ROW }]);
  const setCell = (i, k, v) => setDraft((d) => d.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const rmRow = (i) => setDraft((d) => d.filter((_, idx) => idx !== i));

  const publish = async () => {
    const rows = draft.filter((r) => r.milestone && r.due_date && r.amount)
      .map((r) => ({ milestone: r.milestone, due_date: r.due_date, amount: Number(r.amount), account: r.account }));
    if (!rows.length) return toast.error("Add at least one complete row");
    setBusy(true);
    try {
      await crmApi.post(`/customers/${customerId}/payments`, { rows, replace });
      toast.success(`Published ${rows.length} row(s) — customer notified.`);
      setDraft([{ ...EMPTY_PAYMENT_ROW }]);
      setReplace(false);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const markReceived = async (row) => {
    if (!confirm(`Mark "${row.milestone}" as received? A PDF receipt will be generated and stored in the customer portal (not emailed).`)) return;
    try {
      const { data } = await crmApi.patch(`/customers/${customerId}/payments/${row.id}`, {
        status: "received", received_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(data.auto_generated ? `Marked received · receipt ${data.receipt_number} generated` : "Marked received");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete "${row.milestone}"?`)) return;
    try {
      await crmApi.delete(`/customers/${customerId}/payments/${row.id}`);
      toast.success("Row deleted");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="crm-payment-editor">
      <div className="eyebrow text-teal mb-3">Payment schedule</div>

      {payments.length > 0 && (
        <table className="w-full text-sm mb-6">
          <thead className="text-left text-xs uppercase text-forest/60">
            <tr><th className="p-2">Milestone</th><th className="p-2">Account</th><th className="p-2">Due</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Receipt</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-forest/10" data-testid={`crm-payment-row-${p.id}`}>
                <td className="p-2">{p.milestone}</td>
                <td className="p-2 text-forest/70">{p.account || "Towards Agreement Value"}</td>
                <td className="p-2 text-forest/70">{p.due_date}</td>
                <td className="p-2">₹{Number(p.amount || 0).toLocaleString("en-IN")}</td>
                <td className="p-2">
                  {p.status === "received" ? <span className="text-green-700 font-body">Received</span> :
                    p.status === "submitted" ? <span className="text-amber-700 font-body">Confirmation submitted</span> :
                      <span className="text-forest/60">Due</span>}
                </td>
                <td className="p-2 text-xs text-forest/60 space-x-2">
                  {p.receipt_number || (p.status === "received" ? "—" : "")}
                  {p.proof_id && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/files/${p.proof_id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline" data-testid={`crm-payment-proof-${p.id}`}>View proof</a>}
                </td>
                <td className="p-2 text-right space-x-3">
                  {p.status !== "received" && <button onClick={() => markReceived(p)} className="text-teal text-xs underline" data-testid={`crm-payment-mark-${p.id}`}>Mark received</button>}
                  <button onClick={() => removeRow(p)} className="text-red-600 text-xs underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="border-t border-forest/10 pt-4">
        <div className="text-xs uppercase tracking-widest text-forest/60 mb-3">Add rows to schedule</div>
        {draft.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2">
            <input placeholder="Milestone (e.g. Booking amount)" value={r.milestone} onChange={(e) => setCell(i, "milestone", e.target.value)} className="col-span-4 border border-forest/20 rounded-md px-2 py-1.5 bg-offwhite text-sm" data-testid={`crm-payment-draft-milestone-${i}`} />
            <select value={r.account} onChange={(e) => setCell(i, "account", e.target.value)} className="col-span-3 border border-forest/20 rounded-md px-2 py-1.5 bg-offwhite text-sm" data-testid={`crm-payment-draft-account-${i}`}>
              {PAYMENT_ACCOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <input type="date" value={r.due_date} onChange={(e) => setCell(i, "due_date", e.target.value)} className="col-span-2 border border-forest/20 rounded-md px-2 py-1.5 bg-offwhite text-sm" data-testid={`crm-payment-draft-date-${i}`} />
            <input type="number" min="0" placeholder="₹ amount" value={r.amount} onChange={(e) => setCell(i, "amount", e.target.value)} className="col-span-2 border border-forest/20 rounded-md px-2 py-1.5 bg-offwhite text-sm" data-testid={`crm-payment-draft-amount-${i}`} />
            <button onClick={() => rmRow(i)} className="col-span-1 text-red-600 text-xs">Remove</button>
          </div>
        ))}
        <div className="flex items-center justify-between mt-3">
          <button onClick={addRow} className="text-teal text-sm underline" data-testid="crm-payment-add-row">+ Add row</button>
          <div className="flex items-center gap-4">
            <label className="text-xs text-forest/60 flex items-center gap-2">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Replace existing schedule
            </label>
            <button disabled={busy} onClick={publish} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-cream text-sm disabled:opacity-50" data-testid="crm-payment-publish">
              {busy ? "Publishing…" : "Publish to customer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Bulk upload page (Phase 2) ------------------------------------
function BulkUpload() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [category, setCategory] = useState("receipt");
  const [kind, setKind] = useState("maintenance");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    crmApi.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => s.size === customers.length ? new Set() : new Set(customers.map((c) => c.id)));

  const submit = async () => {
    if (!file) return toast.error("Pick a file");
    if (selected.size === 0) return toast.error("Select at least one customer");
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("customer_ids", [...selected].join(","));
    fd.append("kind", kind);
    try {
      const { data } = await crmApi.post(`/bulk-upload/${category}`, fd);
      setResult(data);
      toast.success(`Pushed to ${data.inserted}/${data.requested} customer(s).`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="crm-bulk-upload">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-teal">Bulk upload</div>
          <h2 className="font-display text-forest text-2xl mt-1">Push one file to many customers</h2>
        </div>
      </div>

      <div className="bg-white border border-forest/15 rounded-lg p-6 grid md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-forest/60">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-bulk-category">
            <option value="receipt">Payment receipt</option>
            <option value="cost_sheet">Cost sheet</option>
            <option value="booking_form">Booking form</option>
            <option value="draft">Draft agreement</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-forest/60">Tag (kind)</label>
          <input value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 bg-offwhite" data-testid="crm-bulk-kind" placeholder="e.g. maintenance_q2_2026" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-forest/60">File (max 5 MB)</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full text-sm" data-testid="crm-bulk-file" />
        </div>
      </div>

      <div className="bg-white border border-forest/15 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-forest/10 bg-forest/5">
          <div className="text-sm font-body text-forest">{selected.size} / {customers.length} selected</div>
          <button onClick={toggleAll} className="text-teal text-sm underline">{selected.size === customers.length ? "Clear all" : "Select all"}</button>
        </div>
        <ul className="max-h-96 overflow-auto">
          {customers.map((c) => (
            <li key={c.id} className="p-3 border-b border-forest/5 flex items-center gap-3 text-sm">
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`crm-bulk-check-${c.id}`} />
              <span className="font-mono text-forest/70 text-xs w-32">{c.customer_id}</span>
              <span className="text-forest flex-1">{c.name}</span>
              <span className="text-forest/60 text-xs">Plot {(c.plot_numbers || []).join(", ")}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between border-t border-forest/10 pt-4">
        <div className="text-xs text-forest/60">One CRM notification email is sent for the whole batch — no spam.</div>
        <button disabled={busy || !file || selected.size === 0} onClick={submit} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-forest text-cream text-sm disabled:opacity-50" data-testid="crm-bulk-submit">
          {busy ? "Pushing…" : `Push to ${selected.size} customer(s)`}
        </button>
      </div>

      {result && <div className="text-sm text-forest/70" data-testid="crm-bulk-result">Pushed to <strong>{result.inserted}</strong> of <strong>{result.requested}</strong> customers.</div>}
    </div>
  );
}

